"use client"

import { useState, useMemo } from "react"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { PurchaseHeader } from "./sections/header"

type PlanId = "6mo" | "1yr"

/** Format a Date as "2 June 2026" */
function formatDate(date: Date): string {
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  })
}

/** Add months to a date, returning a new Date */
function addMonths(date: Date, months: number): Date {
  const result = new Date(date)
  result.setMonth(result.getMonth() + months)
  return result
}

const plans = {
  "6mo": {
    id: "6mo" as PlanId,
    duration: "6-month" as const,
    label: "6-month access",
    licenceType: "standard licence",
    tag: "$ access/6mo",
    price: 7.99,
    perMonth: 1.33,
    strike: null as number | null,
    save: null as number | null,
    best: false,
    durationMonths: 6,
  },
  "1yr": {
    id: "1yr" as PlanId,
    duration: "1-year" as const,
    label: "12-month access",
    licenceType: "extended licence",
    tag: "$ access/12mo",
    price: 12.99,
    perMonth: 1.08,
    strike: 15.98,
    save: 2.99,
    best: true,
    durationMonths: 12,
  },
}

const included = [
  "Installer + 22 templates",
  "8 macOS releases (Catalina → Tahoe)",
  "Automatic script updates",
  "Re-download any template anytime",
  "Email support (24–48h)",
  "Use on 1 Public IP address",
  "Latest Proxmox supported images",
  "Offline template install supported",
]

const faqItems = [
  {
    q: "What exactly do I get?",
    a: "A shell script (~12 KB) that fetches and imports curated VM images straight into Proxmox VE. Plus access to all 22 templates in the library, plus any new templates released during your access window.",
  },
  {
    q: "How is delivery handled?",
    a: "After payment, you receive an email within seconds containing your unique script download URL. The script is hosted securely and tied to your licence and is available in your Client Portal",
  },
  {
    q: "Is this a subscription that auto-renews?",
    a: "No. This is a one-time payment for a fixed access window. There are no recurring charges. When your window expires, you keep what you have downloaded — you just won't receive new updates.",
  },
  {
    q: "What if I extend mid-window?",
    a: "Email us at support@hsve.cc and we will prorate the extension from your current expiry date, not from today.",
  },
  {
    q: "Refund policy?",
    a: "No refunds are processed for the HSVE Templates Script, you need to ensure to read our terms and conditions to learn more.",
  },
  {
    q: "Do I need a licence for Windows / macOS images?",
    a: "Windows images require your own licence key. macOS images are only for use on Apple-licenced hardware (or within the terms of Apple's EULA for virtualisation). We do not provide OS licences.",
  },
  {
    q: "Is this for personal or business use?",
    a: "Both. The standard (6-month) licence covers personal and small-team use on unlimited Proxmox nodes you own. The extended (12-month) licence additionally covers commercial lab environments.",
  },
]

export default function PurchasePage() {
  const [plan, setPlan] = useState<PlanId>("1yr")

  const router = useRouter()
  const p = plans[plan]

  // Calculate dates based on current time
  const today = useMemo(() => new Date(), [])
  const startDateFormatted = useMemo(() => formatDate(today), [today])
  
  // Calculate expiry date for the selected plan
  const expiryDate = useMemo(() => addMonths(today, p.durationMonths), [today, p.durationMonths])
  const expiryDateFormatted = useMemo(() => formatDate(expiryDate), [expiryDate])

  // Calculate expiry dates for each plan (for the plan cards)
  const planExpiries = useMemo(() => ({
    "6mo": formatDate(addMonths(today, 6)),
    "1yr": formatDate(addMonths(today, 12)),
  }), [today])

  function handleCheckout() {
    router.push(`/checkout?plan=${plan}`)
  }

  return (
    <div
      style={{
        background: "var(--bg)",
        minHeight: "100vh",
        color: "var(--text)",
        fontFamily: "var(--font-sans)",
      }}
    >
      <PurchaseHeader />

      {/* ── HERO ── */}
      <section style={{ padding: "64px 48px 56px", borderBottom: "1px solid var(--border)" }}>
        <div style={{ maxWidth: 1240, margin: "0 auto" }}>
          <div
            className="font-mono"
            style={{
              fontSize: 12,
              color: "var(--muted)",
              marginBottom: 20,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <Link href="/templates" style={{ color: "var(--text-2)", textDecoration: "none" }}>
              ← /templates
            </Link>
            <span style={{ color: "var(--faint)" }}>/</span>
            <span style={{ color: "var(--accent)" }}>purchase</span>
          </div>
          <h1
            className="hero-heading"
            style={{
              fontSize: 64,
              fontWeight: 600,
              letterSpacing: "-0.04em",
              lineHeight: 1.0,
              margin: 0,
            }}
          >
            One script.{" "}
            <span style={{ color: "var(--accent)" }}>Two windows.</span>
          </h1>
          <p
            style={{
              fontSize: 17,
              color: "var(--text-2)",
              maxWidth: 680,
              marginTop: 22,
              lineHeight: 1.6,
            }}
          >
            Buy 6 or 12 months of access to the installer and the full 22-template library. All updates
            released during your window are included. Re-run the script anytime, on any of your Proxmox
            nodes.
          </p>
        </div>
      </section>

      {/* ── PLANS + ORDER SUMMARY ── */}
      <section style={{ padding: "64px 48px" }}>
        <div style={{ maxWidth: 1240, margin: "0 auto" }}>
          <div className="hero-grid" style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 32, alignItems: "start" }}>

            {/* LEFT */}
            <div>
              <div
                className="font-mono"
                style={{ fontSize: 11, color: "var(--accent)", letterSpacing: "0.06em", marginBottom: 10 }}
              >
                # choose_plan
              </div>
              <h2 style={{ fontSize: 32, fontWeight: 600, letterSpacing: "-0.025em", marginBottom: 24, marginTop: 0 }}>
                Choose your window.
              </h2>

              {/* Plan cards */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                {(Object.values(plans) as (typeof plans)[keyof typeof plans][]).map((pl) => {
                  const selected = plan === pl.id
                  return (
                    <button
                      key={pl.id}
                      onClick={() => setPlan(pl.id)}
                      style={{
                        textAlign: "left",
                        padding: 22,
                        border: `1px solid ${selected ? "var(--accent)" : "var(--border)"}`,
                        background: selected ? "var(--accent-soft)" : "var(--bg)",
                        borderRadius: 6,
                        cursor: "pointer",
                        position: "relative",
                        display: "flex",
                        flexDirection: "column",
                        transition: "border-color 0.12s, background 0.12s",
                        fontFamily: "var(--font-sans)",
                        color: "var(--text)",
                      }}
                    >
                      {pl.best && (
                        <div
                          className="font-mono"
                          style={{
                            position: "absolute",
                            top: -10,
                            left: 20,
                            fontSize: 10,
                            color: "var(--accent-ink)",
                            background: "var(--accent)",
                            padding: "3px 8px",
                            borderRadius: 3,
                            letterSpacing: "0.06em",
                            fontWeight: 600,
                          }}
                        >
                          BEST VALUE · SAVE 38%
                        </div>
                      )}

                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                        <div>
                          <div className="font-mono" style={{ fontSize: 11, color: "var(--accent)", letterSpacing: "0.06em" }}>
                            {pl.tag}
                          </div>
                          <div style={{ fontSize: 19, fontWeight: 600, marginTop: 6, letterSpacing: "-0.015em" }}>
                            {pl.label}
                          </div>
                          <div className="font-mono" style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}>
                            {pl.licenceType}
                          </div>
                        </div>
                        {/* Radio */}
                        <div
                          style={{
                            width: 18,
                            height: 18,
                            borderRadius: "50%",
                            border: `1.5px solid ${selected ? "var(--accent)" : "var(--border)"}`,
                            background: selected ? "var(--accent)" : "transparent",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          {selected && (
                            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                              <path d="M2 5l2 2 4-4" stroke="var(--accent-ink)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          )}
                        </div>
                      </div>

                      {/* Price */}
                      <div style={{ marginTop: 18, display: "flex", alignItems: "baseline", gap: 8 }}>
                        <span style={{ fontSize: 36, fontWeight: 600, letterSpacing: "-0.025em" }}>
                          £{pl.price.toFixed(2)}
                        </span>
                        {pl.strike && (
                          <span style={{ fontSize: 14, color: "var(--muted)", textDecoration: "line-through" }}>
                            £{pl.strike.toFixed(2)}
                          </span>
                        )}
                      </div>
                      <div className="font-mono" style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}>
                        £{pl.perMonth.toFixed(2)} / month
                      </div>

                      {/* Expiry */}
                      <div
                        style={{
                          marginTop: 18,
                          paddingTop: 14,
                          borderTop: `1px solid ${selected ? "var(--accent-line)" : "var(--border-soft)"}`,
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          fontFamily: "var(--font-mono)",
                          fontSize: 11,
                          color: selected ? "var(--accent)" : "var(--text-2)",
                        }}
                      >
                        <span style={{ color: "var(--muted)" }}>expires</span>
                        {planExpiries[pl.id]}
                      </div>
                    </button>
                  )
                })}
              </div>

              {/* Included panel */}
              <div style={{ marginTop: 28, border: "1px solid var(--border)", borderRadius: 6, overflow: "hidden" }}>
                <div
                  className="font-mono"
                  style={{
                    padding: "12px 20px",
                    fontSize: 11,
                    color: "var(--accent)",
                    background: "var(--bg-soft)",
                    borderBottom: "1px solid var(--border)",
                    letterSpacing: "0.06em",
                  }}
                >
                  # included_in_both_plans
                </div>
                <div
                  style={{
                    padding: "20px 24px",
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "12px 24px",
                    background: "var(--bg)",
                  }}
                >
                  {included.map((item) => (
                    <div
                      key={item}
                      style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 13, color: "var(--text-2)" }}
                    >
                      <span className="font-mono" style={{ color: "var(--success)", flexShrink: 0 }}>✓</span>
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* RIGHT — order summary */}
            <aside style={{ position: "sticky", top: 72 }}>
              <div
                style={{
                  border: "1px solid var(--border)",
                  borderRadius: 6,
                  background: "var(--bg-soft)",
                  overflow: "hidden",
                }}
              >
                <div
                  className="font-mono"
                  style={{
                    padding: "14px 20px",
                    fontSize: 11,
                    color: "var(--accent)",
                    letterSpacing: "0.06em",
                    borderBottom: "1px solid var(--border)",
                    background: "var(--bg)",
                  }}
                >
                  # your_order
                </div>

                <div style={{ padding: 22 }}>
                  {/* Product + price */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16 }}>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 500 }}>Proxmox One-Click VM Templates</div>
                      <div className="font-mono" style={{ fontSize: 11, color: "var(--muted)", marginTop: 4 }}>
                        {p.durationMonths} months · {p.licenceType}
                      </div>
                    </div>
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <div style={{ fontSize: 22, fontWeight: 600, color: "var(--accent)", letterSpacing: "-0.02em" }}>
                        £{p.price.toFixed(2)}
                      </div>
                      {p.save && (
                        <div className="font-mono" style={{ fontSize: 11, color: "var(--success)", marginTop: 2 }}>
                          save £{p.save.toFixed(2)}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Licence meta */}
                  <div
                    className="font-mono"
                    style={{
                      marginTop: 18,
                      padding: "12px 14px",
                      background: "var(--bg)",
                      border: "1px solid var(--border-soft)",
                      borderRadius: 4,
                      fontSize: 12,
                      color: "var(--text-2)",
                      lineHeight: 1.7,
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ color: "var(--muted)" }}>licence_starts</span>
                      <span>{startDateFormatted}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ color: "var(--muted)" }}>licence_expires</span>
                      <span style={{ color: "var(--accent)" }}>{expiryDateFormatted}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span style={{ color: "var(--muted)" }}>delivery</span>
                      <span><span style={{ color: "var(--success)" }}>● </span>instant, by email</span>
                    </div>
                  </div>

                  {/* Totals */}
                  <div
                    className="font-mono"
                    style={{ marginTop: 18, paddingTop: 16, borderTop: "1px solid var(--border-soft)" }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "var(--text-2)" }}>
                      <span>subtotal</span><span>£{p.price.toFixed(2)}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "var(--text-2)", marginTop: 4 }}>
                      <span>VAT (UK)</span><span>calculated at checkout</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: 12, fontSize: 14 }}>
                      <span style={{ color: "var(--text)" }}>total</span>
                      <span style={{ color: "var(--accent)", fontWeight: 600 }}>£{p.price.toFixed(2)}</span>
                    </div>
                  </div>

                  {/* CTA */}
                  <button
                    onClick={handleCheckout}
                    className="font-mono"
                    style={{
                      marginTop: 22,
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      padding: "14px 16px",
                      fontSize: 13,
                      borderRadius: 4,
                      background: "var(--accent)",
                      color: "var(--accent-ink)",
                      border: "none",
                      cursor: "pointer",
                      fontWeight: 500,
                    }}
                  >
                    {`Continue to checkout · £${p.price.toFixed(2)} →`}
                  </button>

                  {/* Trust row */}
                  <div
                    className="font-mono"
                    style={{
                      marginTop: 14,
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: 11,
                      color: "var(--muted)",
                    }}
                  >
                    <span><span style={{ color: "var(--success)" }}>● </span>secure · stripe</span>
                    <span><span style={{ color: "var(--success)" }}>● </span>instant delivery</span>
                    <span><span style={{ color: "var(--success)" }}>● </span>14-day refund</span>
                  </div>
                </div>
              </div>

              {/* Stripe note */}
              <p className="font-mono" style={{ fontSize: 11, color: "var(--muted)", marginTop: 14, lineHeight: 1.7 }}>
                {"// "}You&apos;ll be redirected to Stripe to complete payment. We don&apos;t store card details.
                Email receipt + script URL arrives within seconds.
              </p>
            </aside>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section style={{ padding: "64px 48px", borderTop: "1px solid var(--border)" }}>
        <div style={{ maxWidth: 1240, margin: "0 auto" }}>
          <div className="font-mono" style={{ fontSize: 11, color: "var(--accent)", letterSpacing: "0.06em", marginBottom: 10 }}>
            # purchase_faq
          </div>
          <h2 style={{ fontSize: 36, fontWeight: 600, letterSpacing: "-0.03em", marginBottom: 36, marginTop: 0 }}>
            About the purchase.
          </h2>

          <div style={{ border: "1px solid var(--border)", borderRadius: 6, overflow: "hidden" }}>
            {faqItems.map((item, i) => (
              <details
                key={i}
                style={{ borderBottom: i < faqItems.length - 1 ? "1px solid var(--border)" : "none" }}
              >
                <summary
                  className="font-mono"
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "16px 20px",
                    fontSize: 13,
                    color: "var(--text-2)",
                    cursor: "pointer",
                    listStyle: "none",
                    userSelect: "none",
                    background: "var(--bg)",
                  }}
                >
                  <span>
                    <span style={{ color: "var(--muted)", marginRight: 8 }}>?</span>
                    {item.q}
                  </span>
                  <span style={{ color: "var(--accent)", flexShrink: 0, marginLeft: 16 }}>↗</span>
                </summary>
                <div
                  style={{
                    padding: "0 20px 16px 36px",
                    fontSize: 13,
                    color: "var(--text-2)",
                    lineHeight: 1.7,
                    background: "var(--bg)",
                  }}
                >
                  {item.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer
        style={{
          padding: "32px 48px 28px",
          borderTop: "1px solid var(--border)",
          fontFamily: "var(--font-mono)",
          fontSize: 12,
          display: "grid",
          gridTemplateColumns: "1fr auto",
          gap: 16,
          alignItems: "end",
        }}
      >
        <div style={{ color: "var(--text-2)" }}>
          <div style={{ color: "var(--text)", marginBottom: 6 }}>
            <span style={{ color: "var(--accent)" }}>HSVE</span> :: proxmox consultation &amp; templates :: manchester, uk
          </div>
          <div style={{ color: "var(--muted)" }}>
            support@hsve.cc · discord.gg/hsve · github.com/hsve · © 2026 · v1.86
          </div>
        </div>
        <div style={{ color: "var(--muted)", display: "flex", gap: 18 }}>
          <Link href="/privacy" style={{ color: "var(--muted)", textDecoration: "none" }}>/privacy</Link>
          <Link href="/terms" style={{ color: "var(--muted)", textDecoration: "none" }}>/terms</Link>
          <a href="https://status.hsve.cc" target="_blank" rel="noopener noreferrer" style={{ color: "var(--muted)", textDecoration: "none" }}>/status</a>
        </div>
      </footer>
    </div>
  )
}
